import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  AppSettingsNavigationDTO,
  ClubClassesSettingsDTO,
} from "@gymflow/types";
import { useQuery } from "@tanstack/react-query";
import { apiV2 } from "libs/api/src/lib/apiV2";
import {
  appSettingsHelper,
  FEAppSettingsHomePage,
} from "libs/helpers/src/lib/appSettings";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Route } from "react-router";

import { usePortalRoutes } from "../../../hooks";
import { useClubSettings } from "../../../providers";
import { RouteFeature, RouteLayout } from "../../../routes";
import { Spinner } from "../../atoms";
import PillTabsLayout from "../../molecules/PillTabsLayout";
import { SettingsRoute } from "../SettingsRoute";
import { AppSettingsClasses } from "./AppSettingsClasses/AppSettingsClasses";
import { AppSettingsClassPreview } from "./AppSettingsClasses/AppSettingsClassPreview";
import { AppSettingsHomePage } from "./AppSettingsHomePage/AppSettingsHomePage";
import { AppSettingsNavigation } from "./AppSettingsNavigation/AppSettingsNavigation";
import { AppSettingsNavigationPreview } from "./AppSettingsNavigation/AppSettingsNavigationPreview";
import { AppSettingsContext } from "./components/AppSettingsContext";

export function AppSettings() {
  const { t } = useTranslation();
  const { createClubLink } = usePortalRoutes();
  const { featureFlags } = useClubSettings();
  const navigationSettingsQuery = useQuery(
    apiV2.staff.appSettings.queries.navigation(),
  );
  const [navigationSettings, setNavigationSettings] =
    useState<AppSettingsNavigationDTO>();
  useEffect(() => {
    if (navigationSettingsQuery.data && navigationSettingsQuery.isSuccess) {
      setNavigationSettings(navigationSettingsQuery.data);
    }
  }, [navigationSettingsQuery.data, navigationSettingsQuery.isSuccess]);

  const classesSettingsQuery = useQuery(
    apiV2.staff.appSettings.queries.classes(),
  );
  const [classesSettings, setClassesSettings] =
    useState<ClubClassesSettingsDTO>();
  useEffect(() => {
    if (classesSettingsQuery.data && classesSettingsQuery.isSuccess) {
      setClassesSettings(classesSettingsQuery.data);
    }
  }, [classesSettingsQuery.data, classesSettingsQuery.isSuccess]);

  const homePageSettingsQuery = useQuery(
    apiV2.staff.appSettings.queries.homePage(),
  );
  const [homePageSettings, setHomePageSettings] =
    useState<FEAppSettingsHomePage>();
  useEffect(() => {
    if (homePageSettingsQuery.data && homePageSettingsQuery.isSuccess) {
      setHomePageSettings(
        appSettingsHelper.mapBEtoFE(homePageSettingsQuery.data),
      );
    }
  }, [homePageSettingsQuery.data, homePageSettingsQuery.isSuccess]);

  const isLoading =
    navigationSettingsQuery.isLoading ||
    classesSettingsQuery.isLoading ||
    homePageSettingsQuery.isLoading;
  const hasData = navigationSettings && classesSettings && homePageSettings;
  const [childrenParent] = useAutoAnimate();
  const [parent] = useAutoAnimate();
  return (
    <div className="relative flex size-full">
      <div className="absolute -inset-8 top-0 flex flex-col gap-4 overflow-auto border-t border-gray-200 bg-white p-8 lg:flex-row">
        <div
          ref={parent}
          className="flex size-full gap-6 max-lg:flex-col lg:flex-row"
        >
          {!isLoading && hasData && (
            <AppSettingsContext.Provider
              value={{
                navigationSettings,
                setNavigationSettings,
                classesSettings,
                setClassesSettings,
                homePageSettings,
                setHomePageSettings,
              }}
            >
              <div className="flex h-full rounded-3xl border border-gray-200 p-6 max-lg:w-full lg:w-2/3">
                {isLoading && <Spinner />}
                {!isLoading && hasData && (
                  <PillTabsLayout
                    variant="secondary"
                    tabs={[
                      ...(featureFlags.MEMBER_APP_HOME_PAGE_CUSTOMIZATION
                        ? [
                            {
                              text: t(
                                "page.settings.appSettings.tabs.homePage.navBarTitle",
                              ),
                              path: createClubLink(
                                RouteLayout.Staff,
                                RouteFeature.Settings,
                                SettingsRoute.AppSettings,
                                "home-page",
                              ),
                              component: AppSettingsHomePage,
                            },
                          ]
                        : []),
                      {
                        text: t(
                          "page.settings.appSettings.tabs.navigation.navBarTitle",
                        ),
                        path: createClubLink(
                          RouteLayout.Staff,
                          RouteFeature.Settings,
                          SettingsRoute.AppSettings,
                          "navigation",
                        ),
                        component: AppSettingsNavigation,
                      },
                      {
                        text: t(
                          "page.settings.appSettings.tabs.classes.navBarTitle",
                        ),
                        path: createClubLink(
                          RouteLayout.Staff,
                          RouteFeature.Settings,
                          SettingsRoute.AppSettings,
                          "classes",
                        ),
                        component: AppSettingsClasses,
                      },
                    ]}
                    enableAnimations
                  />
                )}
              </div>
              <div
                ref={childrenParent}
                className="flex w-1/3 min-w-96 max-lg:hidden"
              >
                {isLoading && <Spinner />}
                <Route
                  path={createClubLink(
                    RouteLayout.Staff,
                    RouteFeature.Settings,
                    SettingsRoute.AppSettings,
                    "classes",
                  )}
                >
                  {!classesSettingsQuery.isLoading && (
                    <AppSettingsClassPreview />
                  )}
                </Route>

                <Route
                  path={createClubLink(
                    RouteLayout.Staff,
                    RouteFeature.Settings,
                    SettingsRoute.AppSettings,
                    "(home-page|navigation)",
                  )}
                >
                  {!homePageSettingsQuery.isLoading && (
                    <AppSettingsNavigationPreview />
                  )}
                </Route>
              </div>
            </AppSettingsContext.Provider>
          )}
          {isLoading && <Spinner />}
        </div>
      </div>
    </div>
  );
}
